import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Grid,
  Chip,
  Stack,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { CalendarBooking } from '../../types';
import CalendarDay from './CalendarDay';
import {
  generateCalendarDates,
  createCalendarDay,
  formatMonthYear,
  getNextMonth,
  getPreviousMonth,
  processBookingsForCalendar
} from '../../utils/calendar-utils';

interface CalendarGridProps {
  currentMonth: Date;
  selectedDate: Date | null;
  bookings: CalendarBooking[];
  onDateSelect: (date: Date) => void;
  onMonthChange: (date: Date) => void;
}

const CalendarGrid: React.FC<CalendarGridProps> = ({
  currentMonth,
  selectedDate,
  bookings,
  onDateSelect,
  onMonthChange
}) => {
  const calendarDates = generateCalendarDates(currentMonth);
  const processedBookings = processBookingsForCalendar(bookings);
  const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const handlePreviousMonth = () => {
    onMonthChange(getPreviousMonth(currentMonth));
  };

  const handleNextMonth = () => {
    onMonthChange(getNextMonth(currentMonth));
  };

  const handleKeyNavigation = (event: React.KeyboardEvent) => {
    if (!selectedDate) return;

    let newDate: Date | null = null;
    const currentIndex = calendarDates.findIndex(date => 
      date.toDateString() === selectedDate.toDateString()
    );

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        if (currentIndex > 0) {
          newDate = calendarDates[currentIndex - 1];
        }
        break;
      case 'ArrowRight':
        event.preventDefault();
        if (currentIndex < calendarDates.length - 1) {
          newDate = calendarDates[currentIndex + 1];
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (currentIndex >= 7) {
          newDate = calendarDates[currentIndex - 7];
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (currentIndex < calendarDates.length - 7) {
          newDate = calendarDates[currentIndex + 7];
        }
        break;
    }

    if (newDate) {
      onDateSelect(newDate);
    }
  };

  const hasBookings = bookings.length > 0;

  return (
    <Card sx={{ width: '100%' }}>
      {/* Calendar Header with Navigation */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <IconButton
          onClick={handlePreviousMonth}
          aria-label="Previous month"
          size="small"
        >
          <ChevronLeft />
        </IconButton>

        <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
          {formatMonthYear(currentMonth)}
        </Typography>

        <IconButton
          onClick={handleNextMonth}
          aria-label="Next month"
          size="small"
        >
          <ChevronRight />
        </IconButton>
      </Box>

      <CardContent sx={{ p: 2 }}>
        {/* Week Day Headers */}
        <Grid container spacing={0} sx={{ mb: 1 }}>
          {weekDays.map((day) => (
            <Grid item xs key={day}>
              <Box
                sx={{
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: 'text.secondary',
                  bgcolor: 'grey.50',
                  borderRadius: 0.5,
                }}
              >
                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>{day}</Box>
                <Box sx={{ display: { xs: 'block', sm: 'none' } }}>{day.charAt(0)}</Box>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Calendar Days Grid */}
        <Box
          onKeyDown={handleKeyNavigation}
          tabIndex={-1}
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '1px',
            bgcolor: 'grey.300',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          {calendarDates.map((date) => {
            const calendarDay = createCalendarDay(date, processedBookings, currentMonth, selectedDate);
            return (
              <CalendarDay
                key={`${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`}
                day={calendarDay}
                onClick={onDateSelect}
              />
            );
          })}
        </Box>
      </CardContent>

      {/* Calendar Legend - Only show when there are bookings */}
      {hasBookings && (
        <Box
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'grey.50',
          }}
        >
          <Stack
            direction="row"
            spacing={2}
            sx={{
              flexWrap: 'wrap',
              gap: 1,
              fontSize: '0.75rem',
              color: 'text.secondary',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  bgcolor: 'success.main',
                  borderRadius: '50%',
                }}
              />
              <Typography variant="caption">Check-in</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  bgcolor: 'error.main',
                  borderRadius: '50%',
                }}
              />
              <Typography variant="caption">Check-out</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 6,
                  bgcolor: 'primary.main',
                  borderRadius: 0.25,
                }}
              />
              <Typography variant="caption">Confirmed</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 6,
                  bgcolor: 'warning.main',
                  borderRadius: 0.25,
                }}
              />
              <Typography variant="caption">Pending</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 6,
                  bgcolor: 'secondary.main',
                  borderRadius: 0.25,
                }}
              />
              <Typography variant="caption">External</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Chip
                label="2"
                size="small"
                sx={{
                  width: 16,
                  height: 16,
                  fontSize: '0.625rem',
                  '& .MuiChip-label': { px: 0 },
                }}
              />
              <Typography variant="caption">Multiple</Typography>
            </Box>
          </Stack>
        </Box>
      )}

      {/* Empty state message when no bookings but calendar is visible */}
      {!hasBookings && (
        <Box
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'grey.50',
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No bookings to display for this month
          </Typography>
        </Box>
      )}
    </Card>
  );
};

export default CalendarGrid;